// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    customers: {
      label: 'Clientes',
      help: 'Una fila por ficha de cliente. Es la forma de la cartera, no lo facturado en un periodo',
    },
    dates: {
      created: 'Fecha de alta',
      lastActivity: 'Última actividad',
      lastInvoice: 'Última factura',
    },
    metrics: {
      customers: 'Clientes',
      active: 'Activos',
      prospects: 'Posibles',
      dormant: 'Inactivos',
      lifetimeInvoiced: 'Facturado histórico',
      lifetimePending: 'Pendiente histórico',
      lifetimeQuotes: 'Presupuestos históricos',
      lifetimeAccepted: 'Presupuestos ganados',
      conversion: 'Conversión',
      averageValue: 'Valor medio por cliente',
      creditLimit: 'Crédito concedido',
      rating: 'Valoración media',
    },
    dimensions: {
      sector: 'Sector',
      status: 'Estado',
      kind: 'Empresa o particular',
      country: 'País',
      city: 'Población',
      postalCode: 'Código postal',
      assignedTo: 'Comercial asignado',
      paymentMethod: 'Forma de pago',
      taxRegime: 'Régimen de IVA',
      source: 'De dónde salió la ficha',
      tag: 'Etiqueta',
    },
    filters: {
      rating: 'Valoración',
      creditLimit: 'Crédito concedido',
    },
  },
  ca: {
    customers: {
      label: 'Clients',
      help: 'Una fila per fitxa de client. És la forma de la cartera, no el facturat en un període',
    },
    dates: {
      created: 'Data d\'alta',
      lastActivity: 'Última activitat',
      lastInvoice: 'Última factura',
    },
    metrics: {
      customers: 'Clients',
      active: 'Actius',
      prospects: 'Possibles',
      dormant: 'Inactius',
      lifetimeInvoiced: 'Facturat històric',
      lifetimePending: 'Pendent històric',
      lifetimeQuotes: 'Pressupostos històrics',
      lifetimeAccepted: 'Pressupostos guanyats',
      conversion: 'Conversió',
      averageValue: 'Valor mitjà per client',
      creditLimit: 'Crèdit concedit',
      rating: 'Valoració mitjana',
    },
    dimensions: {
      sector: 'Sector',
      status: 'Estat',
      kind: 'Empresa o particular',
      country: 'País',
      city: 'Població',
      postalCode: 'Codi postal',
      assignedTo: 'Comercial assignat',
      paymentMethod: 'Forma de pagament',
      taxRegime: 'Règim d’IVA',
      source: 'D\'on va sortir la fitxa',
      tag: 'Etiqueta',
    },
    filters: {
      rating: 'Valoració',
      creditLimit: 'Crèdit concedit',
    },
  },
  en: {
    customers: {
      label: 'Customers',
      help: 'One row per customer file. The shape of the customer base, not what was invoiced in a period',
    },
    dates: {
      created: 'Opened',
      lastActivity: 'Last activity',
      lastInvoice: 'Last invoice',
    },
    metrics: {
      customers: 'Customers',
      active: 'Active',
      prospects: 'Prospects',
      dormant: 'Dormant',
      lifetimeInvoiced: 'Lifetime invoiced',
      lifetimePending: 'Lifetime pending',
      lifetimeQuotes: 'Lifetime quotes',
      lifetimeAccepted: 'Quotes won',
      conversion: 'Conversion',
      averageValue: 'Average customer value',
      creditLimit: 'Credit granted',
      rating: 'Average rating',
    },
    dimensions: {
      sector: 'Sector',
      status: 'Status',
      kind: 'Company or person',
      country: 'Country',
      city: 'Town',
      postalCode: 'Postcode',
      assignedTo: 'Assigned to',
      paymentMethod: 'Payment method',
      taxRegime: 'VAT regime',
      source: 'Where the file came from',
      tag: 'Tag',
    },
    filters: {
      rating: 'Rating',
      creditLimit: 'Credit granted',
    },
  },
  fr: {
    customers: {
      label: 'Clients',
      help: 'Une ligne par fiche client. La forme du portefeuille, pas ce qui a été facturé sur une période',
    },
    dates: {
      created: 'Date de création',
      lastActivity: 'Dernière activité',
      lastInvoice: 'Dernière facture',
    },
    metrics: {
      customers: 'Clients',
      active: 'Actifs',
      prospects: 'Prospects',
      dormant: 'Inactifs',
      lifetimeInvoiced: 'Facturé cumulé',
      lifetimePending: 'En attente cumulé',
      lifetimeQuotes: 'Devis cumulés',
      lifetimeAccepted: 'Devis gagnés',
      conversion: 'Conversion',
      averageValue: 'Valeur moyenne par client',
      creditLimit: 'Encours accordé',
      rating: 'Note moyenne',
    },
    dimensions: {
      sector: 'Secteur',
      status: 'Statut',
      kind: 'Entreprise ou particulier',
      country: 'Pays',
      city: 'Ville',
      postalCode: 'Code postal',
      assignedTo: 'Commercial attitré',
      paymentMethod: 'Mode de paiement',
      taxRegime: 'Régime de TVA',
      source: 'Origine de la fiche',
      tag: 'Étiquette',
    },
    filters: {
      rating: 'Note',
      creditLimit: 'Encours accordé',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
